"use client";

import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { CalendarCheck2, CalendarX2, Loader2, Pencil, Sparkles } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { AiCreditLimitBanner } from "@/components/shared/AiCreditLimitBanner";
import { SuggestedDayList, SuggestedDayListSkeleton } from "@/components/shared/SuggestedDayList";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetSection,
  SheetTitle,
} from "@/components/ui/sheet";
import { trackMealSuggest } from "@/lib/analytics";
import { fadeSlideUpVariants } from "@/lib/animations";
import { addDays, formatWeekRangeLabel, parseIsoDate, toIsoDate } from "@/lib/calendar/week";

interface SuggestionRecipe {
  id: string;
  title: string;
  image: string | null;
  imageAspectRatio: number | null;
  publishedAt: string | null;
  tags: string[];
}

interface MealSuggestion {
  recipeId: string;
  date: string;
  isOwned: boolean;
  recipe: SuggestionRecipe;
}

type Step = "prompt" | "loading" | "review";

interface MealSuggestSheetProps {
  isOpen: boolean;
  onClose: () => void;
  locale: string;
  weekStartIso: string;
  todayIso: string;
}

const PROMPT_TEMPLATES = ["templateCasual", "templateHobbyist", "templateAmbitious"] as const;

const TEMPLATE_LABELS = [
  "templateCasualLabel",
  "templateHobbyistLabel",
  "templateAmbitiousLabel",
] as const;

export function MealSuggestSheet({
  isOpen,
  onClose,
  locale,
  weekStartIso,
  todayIso,
}: MealSuggestSheetProps) {
  const t = useTranslations("calendar");
  const tc = useTranslations("common");
  // A server error names a calendar or a common message.
  const errorText = useCallback(
    (error: unknown) => {
      const key = String(error) as never;
      return t.has(key) ? t(key) : tc.has(key) ? tc(key) : t("mealSuggestFailed");
    },
    [t, tc]
  );
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>("prompt");
  const [prompt, setPrompt] = useState("");
  const [promptLoaded, setPromptLoaded] = useState(false);
  const [suggestions, setSuggestions] = useState<MealSuggestion[]>([]);
  /** Why no meals came back: the week already has as many as the preferences allow. */
  const [weekFull, setWeekFull] = useState<{ planned: number; max: number } | null>(null);
  const [usage, setUsage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [regeneratingDay, setRegeneratingDay] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [recipeCount, setRecipeCount] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const [draftPrompt, setDraftPrompt] = useState("");

  const remaining = limit - usage;
  const canGenerate = remaining > 0;

  const weekStart = parseIsoDate(weekStartIso) ?? new Date();
  const weekLabel = formatWeekRangeLabel(weekStart, locale);

  useEffect(() => {
    if (!isOpen) return;
    setStep("prompt");
    setSuggestions([]);
    setWeekFull(null);
    setPromptLoaded(false);
    setRegeneratingDay(null);
    setConfirming(false);
    setEditing(false);

    fetch("/api/meal-suggestions")
      .then((res) => res.json())
      .then((data) => {
        const savedPrompt = data.prompt || "";
        setPrompt(savedPrompt);
        setDraftPrompt(savedPrompt);
        setUsage(data.usage);
        setLimit(data.limit);
        setRecipeCount(data.recipeCount ?? null);
        setEditing(!savedPrompt);
        setPromptLoaded(true);
      })
      .catch(() => setPromptLoaded(true));
  }, [isOpen]);

  const applyTemplate = useCallback(
    (templateKey: string) => {
      setDraftPrompt(t(templateKey as any));
    },
    [t]
  );

  const startEditing = useCallback(() => {
    setDraftPrompt(prompt);
    setEditing(true);
  }, [prompt]);

  const saveEdit = useCallback(() => {
    setPrompt(draftPrompt);
    setEditing(false);
    fetch("/api/meal-suggestions", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prompt: draftPrompt }),
    }).catch(() => {});
  }, [draftPrompt]);

  const cancelEdit = useCallback(() => {
    setDraftPrompt(prompt);
    setEditing(false);
  }, [prompt]);

  // No meals came back: back to the preferences, open for editing.
  const editAfterEmpty = useCallback(() => {
    setStep("prompt");
    startEditing();
  }, [startEditing]);

  const generate = useCallback(async () => {
    let finalPrompt = prompt;
    if (editing) {
      finalPrompt = draftPrompt;
      setPrompt(draftPrompt);
      setEditing(false);
      fetch("/api/meal-suggestions", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: draftPrompt }),
      }).catch(() => {});
    }
    setStep("loading");
    try {
      const res = await fetch("/api/meal-suggestions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          locale,
          weekStartIso,
          todayIso,
          prompt: finalPrompt,
          skipToday: new Date().getHours() >= 21,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        // Over the weekly limit: the prompt step shows the limit banner in place of a toast.
        if (data.error === "aiUsageLimitReached") setUsage(limit);
        else toast.error(errorText(data.error));
        setStep("prompt");
        return;
      }
      trackMealSuggest("generated", { count: data.suggestions.length });
      setSuggestions(data.suggestions);
      setWeekFull(data.weekFull ?? null);
      setUsage(data.usage);
      setLimit(data.limit);
      setStep("review");
    } catch {
      toast.error(t("mealSuggestFailed"));
      setStep("prompt");
    }
  }, [locale, weekStartIso, todayIso, prompt, draftPrompt, editing, limit, errorText, t]);

  const regenerateDay = useCallback(
    async (dayIso: string) => {
      setRegeneratingDay(dayIso);
      try {
        const res = await fetch("/api/meal-suggestions/regenerate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            locale,
            dayIso,
            todayIso,
            weekStartIso,
            prompt,
            currentSuggestions: suggestions.map((s) => ({
              recipeId: s.recipeId,
              date: s.date,
              title: s.recipe.title,
            })),
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          // Over the weekly limit: the review step shows the limit banner in place of a toast.
          if (data.error === "aiUsageLimitReached") setUsage(limit);
          else toast.error(errorText(data.error));
          setRegeneratingDay(null);
          return;
        }
        trackMealSuggest("day_regenerated");
        setSuggestions((prev) => {
          const filtered = prev.filter((s) => s.date !== dayIso);
          return [...filtered, ...data.suggestions].sort((a, b) => a.date.localeCompare(b.date));
        });
        setUsage(data.usage);
        setLimit(data.limit);
      } catch {
        toast.error(t("mealSuggestFailed"));
      } finally {
        setRegeneratingDay(null);
      }
    },
    [locale, todayIso, weekStartIso, prompt, suggestions, limit, errorText, t]
  );

  const removeSuggestion = useCallback((recipeId: string, date: string) => {
    setSuggestions((prev) => prev.filter((s) => !(s.recipeId === recipeId && s.date === date)));
  }, []);

  const confirm = useCallback(async () => {
    if (suggestions.length === 0) return;
    setConfirming(true);
    try {
      const res = await fetch("/api/meal-suggestions/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          suggestions: suggestions.map((s) => ({
            recipeId: s.recipeId,
            date: s.date,
          })),
        }),
      });
      if (!res.ok) {
        toast.error(t("suggestConfirmFailed"));
        setConfirming(false);
        return;
      }
      trackMealSuggest("confirmed", { count: suggestions.length });
      queryClient.invalidateQueries({ queryKey: ["calendar"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(t("suggestSuccess", { count: suggestions.length }));
      onClose();
    } catch {
      toast.error(t("suggestConfirmFailed"));
      setConfirming(false);
    }
  }, [suggestions, queryClient, onClose, t]);

  const localHour = new Date().getHours();
  const futureDays: string[] = [];
  for (let i = 0; i < 7; i++) {
    const day = addDays(weekStart, i);
    const iso = toIsoDate(day);
    if (iso === todayIso && localHour >= 21) continue;
    if (iso >= todayIso) futureDays.push(iso);
  }

  const empty = step === "review" && suggestions.length === 0;

  const suggestionsByDay = new Map<string, MealSuggestion[]>();
  for (const s of suggestions) {
    const list = suggestionsByDay.get(s.date) ?? [];
    list.push(s);
    suggestionsByDay.set(s.date, list);
  }

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent aria-describedby={undefined}>
          <SheetHeader>
            <div className="flex items-center gap-2">
              <Sparkles className="size-5 text-primary" aria-hidden />
              <div>
                <SheetTitle>{t("suggestMealsTitle")}</SheetTitle>
                <SheetDescription>{weekLabel}</SheetDescription>
              </div>
            </div>
          </SheetHeader>

          {step === "review" && !empty && (
            <SheetSection className="pb-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("suggestionsCount", { count: suggestions.length })}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={generate}
                  disabled={!canGenerate}
                  className="gap-1.5 text-xs"
                >
                  <Sparkles className="size-3" aria-hidden />
                  {t("suggestNew")}
                </Button>
              </div>
            </SheetSection>
          )}

          <SheetBody>
            <AnimatePresence mode="wait">
              {step === "prompt" && (
                <motion.div
                  key="prompt"
                  variants={fadeSlideUpVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  className="flex flex-col gap-4"
                >
                  {/* The limit banner sits first in every AI spot, on web and iOS (Jeremy, 2026-10-10). */}
                  {!canGenerate && <AiCreditLimitBanner />}
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {t("mealPreferencesLabel")}
                      </span>
                      {promptLoaded &&
                        (editing ? (
                          <div className="flex gap-1.5">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={cancelEdit}
                              className="h-7 px-2 text-xs"
                            >
                              {t("cancelEdit")}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={saveEdit}
                              className="h-7 px-2 text-xs"
                            >
                              {t("savePreferences")}
                            </Button>
                          </div>
                        ) : !editing ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={startEditing}
                            className="h-7 gap-1 px-2 text-xs"
                          >
                            <Pencil className="size-3" aria-hidden />
                            {t("editPreferences")}
                          </Button>
                        ) : null)}
                    </div>

                    {!promptLoaded ? (
                      <div className="space-y-2">
                        <div className="h-24 animate-pulse rounded-lg bg-muted" />
                        <div className="flex gap-1.5">
                          <div className="h-8 w-28 animate-pulse rounded-full bg-muted" />
                          <div className="h-8 w-24 animate-pulse rounded-full bg-muted" />
                          <div className="h-8 w-32 animate-pulse rounded-full bg-muted" />
                        </div>
                      </div>
                    ) : editing ? (
                      <>
                        <textarea
                          id="meal-prefs"
                          value={draftPrompt}
                          onChange={(e) => setDraftPrompt(e.target.value)}
                          placeholder={t("mealPreferencesPlaceholder")}
                          rows={4}
                          maxLength={2000}
                          className="w-full resize-y rounded-lg border border-border bg-input-bg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        />

                        <p className="mt-2 text-xs text-muted-foreground">{t("templateHint")}</p>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {PROMPT_TEMPLATES.map((templateKey, i) => (
                            <button
                              key={templateKey}
                              type="button"
                              onClick={() => applyTemplate(templateKey)}
                              className="shrink-0 cursor-pointer whitespace-nowrap rounded-full border border-input-border px-3.5 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            >
                              {t(TEMPLATE_LABELS[i] as any)}
                            </button>
                          ))}
                        </div>
                      </>
                    ) : prompt ? (
                      <div className="whitespace-pre-wrap rounded-lg border border-border bg-muted/50 px-3 py-2.5 text-sm text-foreground">
                        {prompt}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">{t("noPreferencesSet")}</p>
                    )}
                  </div>

                  {recipeCount !== null && (
                    <p className="text-xs text-muted-foreground">
                      {recipeCount < 10
                        ? t("suggestRecipeCountLow", { count: recipeCount })
                        : t("suggestRecipeCount", { count: recipeCount })}
                    </p>
                  )}
                </motion.div>
              )}

              {step === "loading" && (
                <motion.div
                  key="loading"
                  variants={fadeSlideUpVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  className="flex flex-col gap-3"
                >
                  <SuggestedDayListSkeleton days={futureDays} todayIso={todayIso} locale={locale} />
                </motion.div>
              )}

              {step === "review" && (
                <motion.div
                  key="review"
                  variants={fadeSlideUpVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  className="flex flex-col gap-3"
                >
                  {/* Never beside the empty message (Jeremy, 2026-10-10): the limit shows again on the prompt step. */}
                  {!canGenerate && !empty && <AiCreditLimitBanner />}
                  {empty ? (
                    // A list of empty days reads like a broken screen, so no meals gets one message.
                    <div className="flex flex-col items-center px-4 py-12 text-center">
                      <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
                        {weekFull ? (
                          <CalendarCheck2 className="size-6" aria-hidden />
                        ) : (
                          <CalendarX2 className="size-6" aria-hidden />
                        )}
                      </div>
                      <h3 className="text-base font-semibold text-foreground">
                        {weekFull ? t("suggestFullTitle") : t("suggestEmptyTitle")}
                      </h3>
                      <p className="mt-1.5 max-w-xs text-sm text-muted-foreground">
                        {weekFull ? t("suggestFullBody", weekFull) : t("suggestEmptyBody")}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-5 gap-1.5"
                        onClick={editAfterEmpty}
                      >
                        <Pencil className="size-3" aria-hidden />
                        {t("suggestEmptyEdit")}
                      </Button>
                    </div>
                  ) : (
                    <SuggestedDayList
                      days={futureDays.map((iso) => ({
                        iso,
                        meals: suggestionsByDay.get(iso) ?? [],
                      }))}
                      todayIso={todayIso}
                      locale={locale}
                      swappingIso={regeneratingDay}
                      swapDisabled={remaining <= 0}
                      onSwap={regenerateDay}
                      onRemove={(s) => removeSuggestion(s.recipeId, s.date)}
                    />
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </SheetBody>

          <SheetFooter>
            {step === "prompt" && (
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={onClose}>
                    {t("suggestCancel")}
                  </Button>
                  <Button
                    className="flex-1 gap-1.5"
                    onClick={generate}
                    disabled={!canGenerate || !promptLoaded}
                  >
                    <Sparkles className="size-3.5" aria-hidden />
                    {t("generateSuggestions")}
                  </Button>
                </div>
              </div>
            )}

            {step === "review" && (
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setStep("prompt")}>
                    {t("suggestCancel")}
                  </Button>
                  {!empty && (
                    <Button className="flex-1" onClick={confirm} disabled={confirming}>
                      {confirming ? (
                        <Loader2 className="size-3.5 animate-spin" aria-hidden />
                      ) : (
                        t("confirmSuggestions")
                      )}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
